'use client'

import { useSearchParams } from 'next/navigation'
import {
  createContext,
  type FC,
  type ReactNode,
  useMemo,
  useState,
} from 'react'

import { useNextPathname } from '../../next-pathname'

export const LOGIN_OTP_STATE = {
  LOGIN_REGISTER: 'login',
  OTP_INPUT: 'otp_input',
  OTP_REQUEST: 'otp_request',
  OTP_REQUEST_NEW_CODE: 'otp_request_new_code',
  PASSWORD_INPUT: 'password_input',
} as const

export type LOGIN_OTP_STATE =
  (typeof LOGIN_OTP_STATE)[keyof typeof LOGIN_OTP_STATE]

interface LoginRegistrationUserLoginState {
  isLoginPage: boolean
  loginOtpState: LOGIN_OTP_STATE
  otpQuery: string
  setLoginOtpState: (loginOtpState: LOGIN_OTP_STATE) => void
  setUsername: (username: string) => void
  username: string
}

// eslint-disable-next-line @typescript-eslint/no-empty-function -- the rule was disabled before a comment became required
const noop = (): void => {}

export const LoginRegistrationUserLoginStateContext =
  createContext<LoginRegistrationUserLoginState>({
    isLoginPage: false,
    loginOtpState: LOGIN_OTP_STATE.LOGIN_REGISTER,
    otpQuery: '',
    setLoginOtpState: noop,
    setUsername: noop,
    username: '',
  })

interface LoginRegistrationUserLoginStateProviderProps {
  children: ReactNode
  guestEmail?: string
}

export const LoginRegistrationUserLoginStateProvider: FC<
  LoginRegistrationUserLoginStateProviderProps
> = ({ children, guestEmail }) => {
  const pathname = useNextPathname()
  const isLoginPage = pathname.includes('/login')

  // if on login page use the query params to set the initial state
  const searchParams = useSearchParams()
  const emailQuery = isLoginPage
    ? (searchParams.get('email') ?? guestEmail ?? '')
    : ''
  const otpQuery = isLoginPage ? (searchParams.get('otp') ?? '') : ''

  const isLoginOtpState = (value: string): value is LOGIN_OTP_STATE => {
    return Object.values(LOGIN_OTP_STATE).includes(value as LOGIN_OTP_STATE)
  }

  // Retrieves the OTP state from URL search parameters, checking both 'otpState' and 'state' (for backward compatibility), and returns a valid LOGIN_OTP_STATE or defaults to LOGIN_REGISTER
  const getOtpState = (): LOGIN_OTP_STATE => {
    // Retrieve the 'otpState' parameter from the URL search parameters
    const otpStateParam = searchParams.get('otpState')
    if (otpStateParam && isLoginOtpState(otpStateParam)) {
      return otpStateParam
    }

    // Adding support for the state query parameter for backward compatibility. There is a production issue where the email link includes state, while the system currently expects otpState. This change ensures that if the state parameter is present, it will be handled gracefully and will not break the OTP flow.
    const stateParam = searchParams.get('state')
    if (stateParam && isLoginOtpState(stateParam)) {
      return stateParam
    }

    return LOGIN_OTP_STATE.LOGIN_REGISTER
  }

  // Determine the initial OTP state based on whether we're on the login page
  const otpState = isLoginPage ? getOtpState() : LOGIN_OTP_STATE.LOGIN_REGISTER

  const [loginOtpState, setLoginOtpState] = useState<LOGIN_OTP_STATE>(otpState)
  const [username, setUsername] = useState(emailQuery)

  const value = useMemo(
    () => ({
      isLoginPage,
      loginOtpState,
      otpQuery,
      setLoginOtpState,
      setUsername,
      username,
    }),
    [
      loginOtpState,
      setLoginOtpState,
      username,
      otpQuery,
      setUsername,
      isLoginPage,
    ],
  )

  return (
    <LoginRegistrationUserLoginStateContext.Provider value={value}>
      {children}
    </LoginRegistrationUserLoginStateContext.Provider>
  )
}
